import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  BusinessProfilesHttpService
} from "../../../../../services/http-services/business-profiles/business-profiles-http.service";
import { DaysOfWeek } from "../../../../../constants/general-variables";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { timeRangeValidator } from "../../../../../validators/form-validators.validator";
import { NgClass, NgForOf, NgIf, SlicePipe } from "@angular/common";
import { DayNameModule } from "../../../../../pipes/day-name/day-name.module";
import { MatCalendar, MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { MatCard } from "@angular/material/card";
import { ChangeWorkTermsModalModule } from "../change-work-terms-modal/change-work-terms-modal.module";
import { CustomSlotPriceComponent } from "../custom-slot-price/custom-slot-price.component";
import {
  FormControlErrorMessagesModule
} from "../../../../../pipes/form-control-error-messages/form-control-error-messages.module";
import { FormControlModule } from "../../../../../pipes/form-control/form-control.module";
import { ToastrService } from "ngx-toastr";
import { ConfirmModalModule } from "../../../../../shared-components/modals/confirm-modal/confirm-modal.module";
import { distinctUntilChanged, map, skip } from 'rxjs';

@Component({
  selector: 'app-slot-management',
  templateUrl: './slot-management.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgForOf,
    DayNameModule,
    NgIf,
    MatCalendar,
    MatCard,
    ChangeWorkTermsModalModule,
    SlicePipe,
    CustomSlotPriceComponent,
    FormControlErrorMessagesModule,
    FormControlModule,
    ConfirmModalModule,
    NgClass
  ]
})
export class SlotManagementComponent implements OnInit, AfterViewInit {
  @Input() urlParams: any;
  workingDayInfo: any;
  protected readonly daysOfWeek = DaysOfWeek;
  initialCheckedDays: any = [];
  termsFirstStepForm!: FormGroup;
  selectedDays: any = [];
  dayMap: Record<string, number> = {
    "monday": 1,
    "tuesday": 2,
    "wednesday": 3,
    "thursday": 4,
    "friday": 5,
    "saturday": 6,
    "sunday": 7
  };
  daySlots: any[] = [];
  selectedSlot: any = null;
  selectDaySlot: any;
  selected: Date | null = null;
  selected2: Date | null = null;
  minDate: any;
  selectedDayData: any;
  isChangeWorkTermsModalOpen = false;
  selectedDateKey: string | null = null;
  selectedDayName: any;
  isAddTimeSlotVisible = false;
  newTimeSlots: any[] = [];
  isAddSubSlotVisible: boolean = false;
  editableSlots: any[] = [];

  setCustomSelected: Date | null = null;
  setCustomSelectedDateKey: string | null = null;
  setCustomSelectedDayData: any;
  setCustomSelectedDayName: any;
  isSetCustomPriceModalOpen = false;
  removeTermIndex: any = null;
  removeTermId: any = null;
  removeSlotIndex: any = null;
  removeSlotId: any = null;
  @ViewChild('calendar') calendar!: MatCalendar<Date>;
  @ViewChild('calendar2') calendar2!: MatCalendar<Date>;
  appointmentsData1: any;
  appointmentsData2: any;

  filteredDate1!: (d: Date | null) => boolean;
  filteredDate2!: ((d: Date | null) => boolean);

  constructor(
    private businessProfilesHttpService: BusinessProfilesHttpService,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.buildForm();
    this.getTimeSlots();
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    this.minDate = tomorrow.toISOString().split('T')[0];
  }

  ngAfterViewInit(): void {
    this.initializeCalendars();
    this.setupListeners();
  }

  private initializeCalendars(): void {
    const range1 = this.getMonthRange(this.calendar.activeDate);
    const range2 = this.getMonthRange(this.calendar2.activeDate);

    this.loadCalendar1(range1.from, range1.to);
    this.loadCalendar2(range2.from, range2.to);
  }

  private setupListeners(): void {
    this.calendar.stateChanges
      .pipe(
        skip(1),
        map(() => this.getMonthRange(this.calendar.activeDate)),
        distinctUntilChanged((p, c) => p.from === c.from)
      )
      .subscribe(range => this.loadCalendar1(range.from, range.to));

    this.calendar2.stateChanges
      .pipe(
        skip(1),
        map(() => this.getMonthRange(this.calendar2.activeDate)),
        distinctUntilChanged((p, c) => p.from === c.from)
      )
      .subscribe(range => this.loadCalendar2(range.from, range.to));
  }

  private getMonthRange(date: Date) {
    const d = date || new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    return {
      from: this.formatDate(new Date(year, month, 1)),
      to: this.formatDate(new Date(year, month + 1, 0))
    };
  }

  loadCalendar1(dateFrom?: string, dateTo?: string) {
    const params = {
      ...this.urlParams,
      date_from: dateFrom || this.formatDate(new Date()),
      date_to: dateTo || this.formatDate(new Date())
    };

    this.businessProfilesHttpService.getNewUserCalendar(params).then(res => {
      this.appointmentsData1 = res;

      this.filteredDate1 = (d: Date | null): boolean => {
        if (!d) return false;
        const dateStr = this.formatDate(d);
        const dayData = this.appointmentsData1[dateStr];
        return !!(dayData && dayData.length > 0);
      };
    });
  }

  loadCalendar2(dateFrom?: string, dateTo?: string) {
    const params = {
      ...this.urlParams,
      date_from: dateFrom || this.formatDate(new Date()),
      date_to: dateTo || this.formatDate(new Date())
    };

    this.businessProfilesHttpService.getNewUserCalendar(params).then(res => {
      this.appointmentsData2 = res;

      this.filteredDate2 = (d: Date | null): boolean => {
        if (!d) return false;
        const dateStr = this.formatDate(d);
        const dayData = this.appointmentsData2[dateStr];
        return !!(dayData && dayData.length > 0);
      };
    });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (d: Date, view) => {
    if (view === 'month') {
      const dateStr = this.formatDate(d);
      const dayData = this.appointmentsData1 ? this.appointmentsData1[dateStr] : null;

      if (!dayData || dayData.length === 0) return '';

      const allDisabled = dayData.every((element: any) =>
        element.slots && element.slots.every((slot: any) => slot.is_disabled === true)
      );

      const totalBookedCount = dayData.reduce((sum: number, element: any) => sum + (element.booked_count || 0), 0);

      if (totalBookedCount > 0) {
        setTimeout(() => {
          const cells = document.querySelectorAll('.booking-calendar .mat-calendar-body-cell');

          cells.forEach(cell => {
            const content = cell.querySelector('.mat-calendar-body-cell-content');
            if (content && content.textContent?.trim() === d.getDate().toString()) {

              if (!cell.querySelector('.booking-badge')) {
                const span = document.createElement('span');
                span.className = 'booking-badge';
                span.innerText = totalBookedCount.toString();
                cell.appendChild(span);
              } else {
                const existingSpan = cell.querySelector('.booking-badge') as HTMLElement;
                existingSpan.innerText = totalBookedCount.toString();
              }
            }
          });
        }, 0);
      }

      return allDisabled ? 'calendar-not-work-date' : '';
    }
    return '';
  };

  private formatDate(d: Date | null): string {
    if (!d) return '';
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  }

  onCalendarDateChange(date: Date | null) {
    this.selected = date;
    this.selectedDateKey = this.formatDate(date);
    this.selectedDayData = this.appointmentsData1[this.selectedDateKey];
    this.selectedDayName = this.selected?.toLocaleDateString('sr-Latn-RS', { weekday: 'long' });
    this.selectedDayName = this.selectedDayName.charAt(0).toUpperCase() + this.selectedDayName.slice(1);
    this.isChangeWorkTermsModalOpen = true;
  }

  onSetCustomPriceCalendarDateChange(date: Date | null) {
    this.setCustomSelected = date;
    this.selected2 = date;
    this.setCustomSelectedDateKey = this.formatDate(date);
    this.setCustomSelectedDayData = this.appointmentsData2[this.setCustomSelectedDateKey];
    this.setCustomSelectedDayName = this.setCustomSelected?.toLocaleDateString('sr-Latn-RS', { weekday: 'long' });
    this.setCustomSelectedDayName = this.setCustomSelectedDayName.charAt(0).toUpperCase() + this.setCustomSelectedDayName.slice(1);
    this.isSetCustomPriceModalOpen = true;
  }

  getTimeSlots(): void {
    this.businessProfilesHttpService.getTimeSlots(this.urlParams.companyId).subscribe((response: any) => {
      this.daySlots = response.data.map((slot: any) => ({
        ...slot,
        weekday: slot.weekday === 7 ? 0 : slot.weekday
      }));

      const activeWeekdayNumbers = this.daySlots.map(item => item.weekday);

      this.initialCheckedDays = DaysOfWeek
        .filter(day => activeWeekdayNumbers.includes(day.number))
        .map(day => day.dayForDB);

      const daysArray = this.termsFirstStepForm.get('days') as FormArray;
      const timesArray = this.termsFirstStepForm.get('individualWorkingTimes') as FormArray;
      const slotsArray = this.termsFirstStepForm.get('individualWorkingSlotsAndBreaks') as FormArray;

      daysArray.clear();
      if (timesArray) timesArray.clear();
      if (slotsArray) slotsArray.clear();

      this.initialCheckedDays.forEach((day: any) => {
        daysArray.push(new FormControl(day));
        if (!this.termsFirstStepForm.value.everyDaySameWorkingTime) {
          timesArray.push(this.createWorkingTimeGroup(day));
        }
        if (!this.termsFirstStepForm.value.everyDaySameSlots) {
          slotsArray.push(this.createSlotAndBreakGroup(day));
        }
      });
    });
  }

  onDaysCheckboxChange(event: any): void {
    const value = event.target.value;
    const checked = event.target.checked;

    this.selectedDays = this.termsFirstStepForm.get('days') as FormArray;

    const index = this.selectedDays.controls.findIndex((c: any) => c.value === value);

    if (checked && index === -1) {
      this.selectedDays.push(new FormControl(value));
    } else if (!checked && index !== -1) {
      this.selectedDays.removeAt(index);
    }
  }

  buildForm(): void {
    this.termsFirstStepForm = this.formBuilder.group({
      days: this.formBuilder.array([]),
      everyDaySameWorkingTime: [false],
      workingTimeStart: [''],
      workingTimeEnd: [''],
      everyDaySameSlots: [false],
      timeSlot: [''],
      breakTime: [''],
      individualWorkingTimes: this.formBuilder.array([]),
      individualWorkingSlotsAndBreaks: this.formBuilder.array([]),
    });
  }

  createSlotAndBreakGroup(day: string, data: any = {}): FormGroup {
    return this.formBuilder.group({
      day: [day],
      timeSlot: [data.time_slot || '', Validators.required],
      breakTime: [data.break_time || '', Validators.required],
    });
  }

  createWorkingTimeGroup(day: string, data: any = {}): FormGroup {
    return this.formBuilder.group({
      day: [day],
      workingTimeStart: [data.working_time_start?.slice(0, -3) || '', [Validators.required, timeRangeValidator]],
      workingTimeEnd: [data.working_time_end?.slice(0, -3) || '', [Validators.required, timeRangeValidator]]
    });
  }

  saveDays(): void {
    const existingSlotsMap = new Map(
      this.daySlots.map((slot: any) => [slot.weekday, slot])
    );

    const selected = this.termsFirstStepForm.get('days')?.value as string[];

    const scheduleArray = selected.map((day: string) => {
      const dayKey = day.toLowerCase();
      const weekdayFromMap = this.dayMap[dayKey];

      const lookupKey = (weekdayFromMap === 7 || weekdayFromMap === 0) ? 0 : weekdayFromMap;

      const existingSlot: any = existingSlotsMap.get(lookupKey);

      const backendWeekday = (lookupKey === 0) ? 7 : lookupKey;

      const item: any = {
        weekday: backendWeekday,
        work_from: existingSlot ? this.toHiFormat(existingSlot.work_from) : "09:00",
        work_to: existingSlot ? this.toHiFormat(existingSlot.work_to) : "17:00"
      };

      if (existingSlot && existingSlot.id) {
        item.id = existingSlot.id;
      }

      return item;
    });

    const payload = {
      working_days: scheduleArray
    };

    this.businessProfilesHttpService.saveWorkingDays(payload, this.urlParams.companyId)
      .subscribe({
        next: (response: any) => {
          this.getTimeSlots();
          this.daySlots = [];
          this.toastrService.success(response.message)
        },
        error: (err) => console.error('Greška:', err)
      });
  }

  selectSlot(day: any) {
    const workDay = this.getWorkDay(day.number);
    if (!workDay) return;

    this.selectedSlot = workDay;
    this.selectDaySlot = '';
    this.fetchWorkingDayInfo(workDay.id);
  }

  saveChanges() {
    console.log('this.daySlots', this.daySlots)
    const payload = {
      working_days: this.daySlots.map((slot: any) => ({
        ...slot,
        weekday: slot.weekday === 0 ? 7 : slot.weekday,
        work_from: this.toHiFormat(slot.work_from),
        work_to: this.toHiFormat(slot.work_to),
      }))
    };

    this.businessProfilesHttpService.saveWorkingDays(payload, this.urlParams.companyId)
      .subscribe({
        next: (response: any) => {
          this.getTimeSlots();
          this.toastrService.success(response.message)
        },
        error: (err) => console.error('Greška:', err)
      });
  }

  fetchWorkingDayInfo(workingDay: any): void {
    this.businessProfilesHttpService.getWorkingDayInfo(this.urlParams.companyId, workingDay).subscribe((response: any) => {
      this.workingDayInfo = null;
      this.workingDayInfo = response.data;
      const foundObj = this.workingDayInfo.find((item: any) => item.id === this.selectDaySlot.id);
      this.selectDaySlot = foundObj;
      this.editableSlots = this.selectDaySlot?.slots.map((s: any) => ({
        id: s.id,
        description: s.description,
        base_price: s.base_price,
        max_bookings: s.max_bookings
      }));
    });
  }

  addTimeRow() {
    this.isAddTimeSlotVisible = true;
    this.newTimeSlots.push({
      start_time: '09:00',
      end_time: '17:00'
    });
  }

  removeTimeRow(index: number) {
    this.newTimeSlots.splice(index, 1);
    if (this.newTimeSlots.length === 0) {
      this.isAddTimeSlotVisible = false;
    }
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  isSlotWithinWorkingHours(slot: any): boolean {
    if (!this.selectedSlot) return false;
    const start = this.timeToMinutes(slot.start_time);
    const end = this.timeToMinutes(slot.end_time);
    const workFrom = this.timeToMinutes(this.selectedSlot.work_from);
    const workTo = this.timeToMinutes(this.selectedSlot.work_to);
    return start >= workFrom && end <= workTo && start < end;
  }

  saveAllTimeSlots(selectedDayId: number) {
    const allWithinWorkingHours = this.newTimeSlots.every(slot => this.isSlotWithinWorkingHours(slot));
    if (!allWithinWorkingHours) {
      alert('Neki od novih termina su van radnog vremena!');
      return;
    }
    const existingSlots = this.workingDayInfo.map((info: any) => ({
      start_time: info.start_time.slice(0, 5),
      end_time: info.end_time.slice(0, 5),
      id: info.id
    }));
    const newlyAddedSlots = this.newTimeSlots.map((slot: any) => ({
      start_time: slot.start_time,
      end_time: slot.end_time
    }));
    const combinedSlots = [...existingSlots, ...newlyAddedSlots];
    const payload = {
      time_slots: combinedSlots
    };
    this.businessProfilesHttpService.saveTimeSlotForTerm(this.urlParams.companyId, selectedDayId, payload)
      .subscribe({
        next: (res: any) => {
          this.toastrService.success(res.message);
          this.newTimeSlots = [];
          this.isAddTimeSlotVisible = false;
          this.fetchWorkingDayInfo(selectedDayId);
        },
        error: (err) => {
          console.error('Došlo je do greške pri čuvanju:', err);
          this.toastrService.error(err.error.errors.time_slots[0]);
        }
      });
  }

  allSlotsValid(): boolean {
    return this.newTimeSlots.every(slot => this.isSlotWithinWorkingHours(slot));
  }

  selectDaySlott(info: any) {
    this.selectDaySlot = info;
    this.editableSlots = info.slots.map((s: any) => ({
      id: s.id,
      description: s.description,
      base_price: s.base_price,
      max_bookings: s.max_bookings
    }));
    this.isAddSubSlotVisible = true;
    this.getTimeSlotsForTermin();
  }

  addSubSlotRow() {
    this.editableSlots.push({
      id: null,
      description: '',
      base_price: 0,
      max_bookings: 1
    });
  }

  saveSubSlots() {
    const payload = {
      slots: this.editableSlots.map(s => {
        const slotObj: any = {
          description: s.description,
          base_price: s.base_price,
          max_bookings: s.max_bookings
        };
        if (s.id) {
          slotObj.id = s.id;
        }
        return slotObj;
      })
    };

    console.log('payload', payload)
    this.businessProfilesHttpService.saveTimeSlotForTermTimeslot(this.urlParams.companyId, this.selectedSlot.id, this.selectDaySlot.id, payload)
      .subscribe(res => {
        this.toastrService.success(res.message);
        this.fetchWorkingDayInfo(this.selectedSlot.id);
      });
  }

  getTimeSlotsForTermin(): void {
    this.businessProfilesHttpService.getTimeSlotForTermTimeslot(this.urlParams.companyId, this.selectedSlot.id, this.selectDaySlot.id).subscribe(res => {
      this.fetchWorkingDayInfo(this.selectedSlot.id);
    })
  }

  isFormValid(): boolean {
    return this.workingDayInfo.every((slot: any) => this.isSlotWithinWorkingHours(slot));
  }

  saveeeChanges(selectedDayId: any) {
    if (this.isFormValid()) {
      const payload = {
        time_slots: this.workingDayInfo.map((s: any) => {
          const slotObj: any = {
            start_time: this.toHiFormat(s.start_time),
            end_time: this.toHiFormat(s.end_time),
            weekday: this.selectedSlot.weekday === 0 ? 7 : this.selectedSlot.weekday
          };
          if (s.id) {
            slotObj.id = s.id;
          }
          return slotObj;
        })
      };

      this.businessProfilesHttpService.saveTimeSlotForTerm(this.urlParams.companyId, selectedDayId, payload)
        .subscribe({
          next: (res: any) => {
            this.toastrService.success(res.message);
            this.newTimeSlots = [];
            this.isAddTimeSlotVisible = false;
            this.fetchWorkingDayInfo(selectedDayId);
          },
          error: (err) => {
            console.error('Došlo je do greške pri čuvanju:', err);
          }
        });
    }
  }

  private toHiFormat(time: string): string {
    if (!time) return time;
    const parts = time.split(':');
    if (parts.length === 3) {
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  }

  isSlotClickable(day: any): boolean {
    return !!this.getWorkDay(day.number);
  }

  openRemoveTermMOdal(index: number, selectedDayId: any): void {
    this.removeTermId = selectedDayId;
    this.removeTermIndex = index;
  }

  removeeTimeRow() {
    const termToDelete = this.workingDayInfo[this.removeTermIndex];
    if (!termToDelete) return;
    this.workingDayInfo.splice(this.removeTermIndex, 1);
    this.businessProfilesHttpService.deleteTerm(this.urlParams.companyId, this.removeTermId, termToDelete.id)
      .subscribe({
        next: (res: any) => {
          this.toastrService.success(res.message);
          this.newTimeSlots = [];
          this.isAddTimeSlotVisible = false;
          this.removeTermId = null;
          this.removeTermIndex = null;
          this.selectDaySlot = null;
        },
        error: (err) => {
          console.error('Došlo je do greške pri čuvanju:', err);
        }
      });
  }

  removeeSlotRow() {
    this.editableSlots.splice(this.removeSlotIndex, 1);
    this.businessProfilesHttpService.deleteSlot(this.urlParams.companyId, this.removeTermId, this.removeTermIndex, this.removeSlotId)
      .subscribe({
        next: (res: any) => {
          this.toastrService.success(res.message);
          this.removeSlotIndex = null;
          this.removeSlotId = null;
          this.removeTermId = null;
          this.removeTermIndex = null;
        },
        error: (err) => {
          console.error('Došlo je do greške pri čuvanju:', err);
        }
      });
  }

  openRemoveSlotMOdal(index: number, slot: any, selectedDayId: any): void {
    this.removeSlotIndex = index;
    this.removeSlotId = slot.id;
    this.removeTermId = selectedDayId;
    this.removeTermIndex = this.selectDaySlot.id;
  }

  getWorkDay(dayNumber: number) {
    return this.daySlots?.find((d: any) => d.weekday === dayNumber);
  }

}
